import React, { useEffect, useRef } from 'react';
import { X, Mic, MicOff, Video, VideoOff, PhoneOff, Maximize, Settings, ClipboardList, ExternalLink } from 'lucide-react';

const VideoCallModal = ({ isOpen, onClose, appointment }) => {
  const jitsiContainerRef = useRef(null);
  const jitsiApiRef = useRef(null);
  const role = localStorage.getItem('userRole') || 'user';

  const openClinicalNotes = () => {
    window.open(`/clinical-encounter/${appointment.id}`, '_blank', 'width=1200,height=900');
  };

  useEffect(() => {
    if (isOpen && appointment && jitsiContainerRef.current) {
      console.log('VideoCallModal: Connecting to session:', appointment);
      
      const apptId = appointment.id || appointment.appointmentId || 'na';
      
      // Use ONLY a clean, unique Appointment ID for the room to ensure both parties land in the exact same spot
      const cleanRoomName = `UnumedTelehealthRoomSession${apptId}`.replace(/[^a-zA-Z0-9]/g, '');

      const domain = 'meet.jit.si';
      const options = {
        roomName: cleanRoomName,
        width: '100%',
        height: '100%',
        parentNode: jitsiContainerRef.current,
        userInfo: {
          displayName: localStorage.getItem('userName') || (role === 'patient' ? 'Patient' : 'Doctor'),
        },
        configOverwrite: {
          startWithAudioMuted: false,
          prejoinPageEnabled: false, // Bypass "Join Meeting" screen for speed
          disableModeratorIndicator: true,
          enableEmailInStats: false,
        },
        interfaceConfigOverwrite: {
          DISABLE_JOIN_LEAVE_NOTIFICATIONS: true,
          SHOW_JITSI_WATERMARK: false,
          SHOW_WATERMARK_FOR_GUESTS: false,
          TOOLBAR_BUTTONS: ['microphone', 'camera', 'desktop', 'fullscreen', 'fittowindow', 'hangup', 'chat', 'settings', 'tileview', 'videobackgroundblur']
        },
      };

      // Ensure the script is loaded
      if (window.JitsiMeetExternalAPI) {
        jitsiApiRef.current = new window.JitsiMeetExternalAPI(domain, options);

        jitsiApiRef.current.addEventListeners({
          readyToClose: () => {
            onClose();
          },
          videoConferenceLeft: () => {
             onClose();
          }
        });
      }

      return () => {
        if (jitsiApiRef.current) {
          jitsiApiRef.current.dispose();
        }
      };
    }
  }, [isOpen, appointment, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/95 backdrop-blur-xl p-4 md:p-8 animate-in fade-in duration-500">
      <div className="bg-slate-900 w-full h-full max-w-7xl rounded-[48px] shadow-2xl overflow-hidden flex flex-col relative border border-white/10">
        
        {/* Call Header */}
        <div className="absolute top-0 left-0 w-full p-8 flex justify-between items-center z-10 bg-gradient-to-b from-slate-900/80 to-transparent">
          <div className="flex items-center gap-4">
             <div className="w-12 h-12 rounded-2xl bg-purple-600 flex items-center justify-center shadow-2xl shadow-purple-500/20">
                <Video className="w-6 h-6 text-white" />
             </div>
             <div>
                <h2 className="text-xl font-black text-white tracking-tight">
                    {appointment.patientName || `${appointment.Patient?.firstName} ${appointment.Patient?.lastName}`}
                </h2>
                <div className="flex items-center gap-2 mt-1">
                    <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    <p className="text-[10px] font-black text-emerald-400 uppercase tracking-widest">Secure Telehealth Session</p>
                </div>
             </div>
          </div>

          <div className="flex items-center gap-4 text-white">
             {role !== 'patient' && (
                <button 
                   onClick={openClinicalNotes}
                   className="p-4 bg-white/10 hover:bg-white/20 text-white rounded-3xl transition-all shadow-xl active:scale-95 flex items-center gap-2 group"
                >
                   <ClipboardList className="w-5 h-5" />
                   <span className="text-[11px] font-black uppercase tracking-widest hidden sm:inline">Clinical Notes</span>
                   <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-all" />
                </button>
             )}
             <div className="hidden md:flex items-center gap-3 bg-white/5 backdrop-blur-md px-5 py-2.5 rounded-2xl border border-white/10">
                <div className="flex flex-col items-end">
                    <p className="text-[9px] font-black text-white/40 uppercase tracking-widest">Practitioner</p>
                    <p className="text-xs font-bold text-white uppercase italic">Dr. {appointment.doctorName || appointment.Doctor?.username}</p>
                </div>
                <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center text-indigo-400 font-black text-[10px]">
                    MD
                </div>
             </div>
             <button 
                onClick={onClose}
                className="p-4 bg-red-600 hover:bg-red-700 text-white rounded-3xl transition-all shadow-xl shadow-red-500/20 active:scale-95 group flex items-center gap-2"
              >
                <PhoneOff className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                <span className="text-[11px] font-black uppercase tracking-widest hidden sm:inline">End Session</span>
              </button>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 w-full bg-slate-800 relative flex overflow-hidden">
            {/* Jitsi Iframe Container */}
            <div className="w-full h-full relative">
                <div id="jitsi-root" ref={jitsiContainerRef} className="w-full h-full" />
                
                {!window.JitsiMeetExternalAPI && (
                    <div className="absolute inset-0 flex items-center justify-center text-white flex-col gap-4 bg-slate-800/80 backdrop-blur-md">
                        <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
                        <p className="text-sm font-bold animate-pulse">Initializing Secure Video Link...</p>
                    </div>
                )}
            </div>
        </div>

        {/* Subtle Bottom Controls Overlay (UI Visual Only, Jitsi has its own) */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-6 z-10 opacity-0 pointer-events-none">
            <button className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-xl border border-white/20 text-white flex items-center justify-center hover:bg-white/20 transition-all"><Mic className="w-5 h-5"/></button>
            <button className="w-16 h-16 rounded-full bg-red-600 text-white flex items-center justify-center hover:bg-red-700 transition-all shadow-xl shadow-red-500/20"><PhoneOff className="w-6 h-6"/></button>
            <button className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-xl border border-white/20 text-white flex items-center justify-center hover:bg-white/20 transition-all"><VideoOff className="w-5 h-5"/></button>
        </div>

        <div className="absolute bottom-8 right-8 text-[9px] font-black text-white/20 uppercase tracking-[0.3em] pointer-events-none">
            Unumed Cryptographic Layer v4.0
        </div>
      </div>
    </div>
  );
};

export default VideoCallModal;
